<!--
  TaskRelatedRecordsField.vue

  Search-and-tag picker for related records used by AddTaskModal and
  TaskDetailModal. Owns search + autocomplete; the caller owns the persisted
  list and reacts to add/remove events. This split keeps create-time deferred
  saving and edit-time immediate persistence cleanly separated.
-->
<template>
  <div class="task-related-records">
    <div v-if="records.length" class="flex flex-wrap g-2 mb-2">
      <span v-for="rec in records" :key="rec.id" class="tag is-light">
        <span>{{ rec.name }}</span>
        <span v-if="rec.type" class="ml-1 text-muted">({{ rec.type }})</span>
        <button v-if="!readOnly" class="delete is-small ml-1" @click="emit('remove', rec.id)" />
      </span>
    </div>
    <AutocompleteInput
      v-if="!readOnly"
      :results="searchResults"
      :placeholder="placeholder"
      :size="size"
      bordered
      @search="handleSearch"
      @select="handleSelect"
    />
  </div>
</template>

<script lang="ts">
import type { BaseRecordFragment } from "@auger/core/gql/types"

/** A related record as the picker lists it: an id, and a name and type to show. */
export type RelatedRecord = Pick<BaseRecordFragment, "id"> &
  Partial<Pick<BaseRecordFragment, "name" | "type">>
</script>

<script setup lang="ts">
import { computed, ref } from "vue"
import { toast } from "vue-sonner"
import type { Records_Bool_Exp } from "@auger/core/gql/types"
import AutocompleteInput from "@auger/ui/components/AutocompleteInput.vue"
import { searchRecordsForTaskLink } from "@/common/hasura/records"
import { client } from "@/init/apollo"
import { parseSearchQuery } from "@/utils/searchParser"

const TYPE_ALIASES: Record<string, string> = {
  org: "organization",
  company: "organization",
  person: "person",
  people: "person",
  event: "event",
  list: "list",
  task: "task",
  doc: "document",
  document: "document",
}

const props = defineProps({
  records: { type: Array as () => RelatedRecord[], default: (): RelatedRecord[] => [] },
  excludeIds: { type: Array as () => number[], default: (): number[] => [] },
  placeholder: { type: String, default: "Search... (try is:org, is:person)" },
  size: { type: String, default: "normal" },
  readOnly: { type: Boolean, default: false },
})
const emit = defineEmits<{ add: [record: RelatedRecord]; remove: [id: number] }>()

const searchResults = ref<[RelatedRecord, string][]>([])

const allExcludedIds = computed(
  () => new Set([...props.records.map((r) => r.id), ...props.excludeIds])
)

function handleSearch(rawQuery: string) {
  if (!rawQuery || rawQuery.length < 2) {
    searchResults.value = []
    return
  }

  const parsed = parseSearchQuery(rawQuery)
  const nameQuery = parsed.name || ""
  if (!nameQuery && !parsed.type) {
    searchResults.value = []
    return
  }

  const where: Records_Bool_Exp = { archived_at: { _is_null: true } }
  if (nameQuery) {
    where._or = [
      { name: { _ilike: `%${nameQuery}%` } },
      { description: { _ilike: `%${nameQuery}%` } },
    ]
  }
  if (parsed.type) {
    where.type = { _eq: TYPE_ALIASES[parsed.type] || parsed.type }
  }

  client
    .query({ query: searchRecordsForTaskLink, variables: { where } })
    .then(({ data }) => {
      searchResults.value = (data.records || [])
        .filter((r) => !allExcludedIds.value.has(r.id))
        .map((r): [RelatedRecord, string] => [r, `${r.name} (${r.type})`])
    })
    .catch((err) => {
      console.error("TaskRelatedRecordsField search failed:", err)
      searchResults.value = []
      toast.error("Search failed. Please try again.")
    })
}

function handleSelect(selection: [RelatedRecord, string]) {
  if (!selection) return
  const record = selection[0]
  if (!record || allExcludedIds.value.has(record.id)) return
  emit("add", record)
  searchResults.value = []
}
</script>
